import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import FleetVehicleSelect from "./FleetVehicleSelect";
import {
  vmPageShell,
  vmTableScrollWrap,
  vmRecordRowBg,
  getVmRecordTableStyle,
  getVmRecordThStyle,
  getVmRecordTdStyle,
  vmRecordColGroupReact,
} from "./vehicleManagementLayout";
import { FaEye } from "react-icons/fa";
import { resolveUploadUrl } from "../../lib/resolveUploadUrl";

const BASE = API_BASE_URL;

const DOC_TYPES = [
  { key: "RegistrationCertificate", label: "Registration Certificate" },
  { key: "Insurance",               label: "Insurance"               },
  { key: "FitnessCertificate",      label: "Fitness"                 },
  { key: "RoadTax",                 label: "Road Tax"                },
  { key: "PollutionCertificate",    label: "Pollution"               },
  { key: "StatePermit",             label: "State Permit"            },
  { key: "Permit",                  label: "National Permit"         },
  { key: "TemporaryPermit",         label: "Temporary Permit"        },
];

const EXPIRY_FIELD = {
  RegistrationCertificate: "registrationDate",
  Insurance:               "insuranceValidUpto",
  FitnessCertificate:      "fitnessValidUpto",
  RoadTax:                 "taxValidUpto",
  PollutionCertificate:    "pollutionValidUpto",
  StatePermit:             "statePermitValidUpto",
  Permit:                  "permitUpto",
  TemporaryPermit:         "TemporarypermitUpto",
};

/** Short labels for compact one-row-per-vehicle document tiles */
const DOC_SHORT = {
  RegistrationCertificate: "RC",
  Insurance: "INS",
  FitnessCertificate: "FIT",
  RoadTax: "TAX",
  PollutionCertificate: "PUC",
  StatePermit: "SP",
  Permit: "NP",
  TemporaryPermit: "TP",
};

const DOC_RECORD_COL_COUNT = 3 + DOC_TYPES.length;

/** One list item per vehicle; each `docs[key]` holds latest row data for that document type */
function buildVehicleRows(data, apiBase) {
  const root = apiBase.replace(/\/$/, "");
  return data.map((v) => {
    const docs = {};
    for (const { key, label } of DOC_TYPES) {
      const history = (v.documentHistory && v.documentHistory[key]) || [];
      const latest = history.length ? history[history.length - 1] : null;
      const fileUrl =
        v.filePaths && v.filePaths[key]
          ? v.filePaths[key]
          : v[key]
            ? `${root}/${String(v[key]).replace(/^\//, "")}`
            : null;
      const expField = EXPIRY_FIELD[key];
      const endDate = latest?.expiryDate || (expField ? v[expField] : null) || null;
      const source = latest?.source || (v[key] ? "onboarding" : null);
      docs[key] = {
        documentKey: key,
        documentType: label,
        fromDate: latest?.issueDate || null,
        endDate,
        remarks: latest?.remarks || "",
        fileUrl,
        source,
        historyCount: history.length,
        isRenewed: source === "renewal",
      };
    }
    return {
      vehicleNumber: v.vehicleNumber,
      registerName: v.registerName || "",
      docs,
    };
  });
}

const docEyeMenuPopover = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 40,
  minWidth: 148,
  padding: "8px 10px",
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: 6,
  boxShadow: "0 6px 16px rgba(0,0,0,.14)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  alignItems: "stretch",
};

function DocsDesktopColGroup() {
  return (
    <colgroup>
      <col className="docs-col-check" style={{ width: "3%" }} />
      <col className="docs-col-vehicle" style={{ width: "9%" }} />
      <col className="docs-col-owner" style={{ width: "8%" }} />
      {DOC_TYPES.map(({ key }) => (
        <col key={key} className="docs-col-doc" style={{ width: "10%" }} />
      ))}
    </colgroup>
  );
}

/** Yellow tile + black eye — matches Diesel Actions trigger (documents only). */
function DocumentEyeTileButton({ onClick, open = false, ariaLabel, title, disabled }) {
  return (
    <button
      type="button"
      className={
        "docs-doc-eye-tile" + (open ? " docs-doc-eye-tile--open" : "")
      }
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      aria-expanded={open}
    >
      <FaEye aria-hidden style={{ color: "#111" }} />
    </button>
  );
}

function DocumentEyeTileLink({ href, ariaLabel, title }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="docs-doc-eye-tile"
      aria-label={ariaLabel}
      title={title}
    >
      <FaEye aria-hidden style={{ color: "#111" }} />
    </a>
  );
}

function DocumentEyeMenu({ viewUrl, canHistory, onHistory, statusBadge, endDate, label }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="docs-doc-eye-wrap" style={{ position: "relative", display: "inline-flex" }}>
      <DocumentEyeTileButton
        open={open}
        onClick={() => setOpen((v) => !v)}
        ariaLabel={open ? `Close menu for ${label}` : `Open menu for ${label}`}
        title={open ? "Close" : "View / history"}
      />
      {open ? (
        <div className="docs-doc-eye-menu" style={docEyeMenuPopover} role="menu">
          {viewUrl ? (
            <button
              type="button"
              role="menuitem"
              style={{ fontSize: 11, fontWeight: 700, padding: "6px 8px", borderRadius: 4, border: "1px solid #1565c0", background: "#e3f2fd", color: "#1565c0", cursor: "pointer" }}
              onClick={() => {
                window.open(viewUrl, "_blank", "noopener,noreferrer");
                setOpen(false);
              }}
            >
              View document
            </button>
          ) : (
            <span style={{ fontSize: 11, color: "#999" }}>No file</span>
          )}
          <button
            type="button"
            role="menuitem"
            disabled={!canHistory}
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "6px 8px",
              borderRadius: 4,
              border: "1px solid #1565c0",
              background: canHistory ? "#e3f2fd" : "#f5f5f5",
              color: canHistory ? "#1565c0" : "#aaa",
              cursor: canHistory ? "pointer" : "not-allowed",
              opacity: canHistory ? 1 : 0.55,
            }}
            onClick={() => {
              if (!canHistory) return;
              setOpen(false);
              onHistory();
            }}
          >
            Previous
          </button>
          <div style={{ textAlign: "center", paddingTop: 2 }}>{statusBadge(endDate)}</div>
        </div>
      ) : null}
    </div>
  );
}

/** Per doc-type column cell (header row shows RC / INS / …). */
function DocumentCell({ row, docKey, label, S, fmt, statusBadge, openHistory }) {
  const d = row.docs[docKey];
  const canHistory = d.historyCount > 0 || !!d.fileUrl;
  const cellStyle = d.isRenewed ? { ...S.docCell, ...S.docCellRenewed } : S.docCell;
  const viewUrl = resolveUploadUrl(BASE, d.fileUrl);

  return (
    <div className="docs-doc-cell" style={cellStyle}>
      <div style={S.docCellTags}>
        {d.source === "onboarding" ? (
          <span style={S.srcTagCompact}>onb</span>
        ) : d.isRenewed ? (
          <span style={S.srcTagGreenCompact}>ren</span>
        ) : (
          <span style={S.srcTagMuted}>—</span>
        )}
      </div>
      <div style={S.docCellExpiry}>
        <span style={{ color: "#666", fontSize: 10 }}>Upto</span>{" "}
        <b style={{ fontSize: 11 }}>{fmt(d.endDate)}</b>
      </div>
      <div style={S.docCellStatus}>{statusBadge(d.endDate)}</div>
      <div style={S.docCellActions}>
        <DocumentEyeMenu
          viewUrl={viewUrl}
          canHistory={canHistory}
          onHistory={() =>
            openHistory({
              vehicleNumber: row.vehicleNumber,
              documentKey: d.documentKey,
              documentType: d.documentType,
            })
          }
          statusBadge={statusBadge}
          endDate={d.endDate}
          label={label}
        />
      </div>
    </div>
  );
}

const MONTHS = [
  { value: "",   label: "All Months"  },
  { value: "1",  label: "January"    },
  { value: "2",  label: "February"   },
  { value: "3",  label: "March"      },
  { value: "4",  label: "April"      },
  { value: "5",  label: "May"        },
  { value: "6",  label: "June"       },
  { value: "7",  label: "July"       },
  { value: "8",  label: "August"     },
  { value: "9",  label: "September"  },
  { value: "10", label: "October"    },
  { value: "11", label: "November"   },
  { value: "12", label: "December"   },
];

const currentYear = new Date().getFullYear();
const YEARS = ["", ...Array.from({ length: 10 }, (_, i) => String(currentYear - 3 + i))];

const today = () => new Date().toISOString().split("T")[0];
const fmt   = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const emptyDocEntry = () => ({ fromDate: "", endDate: "", file: null });

const emptyForm = {
  submitDate:    today(),
  vehicleNumber: "",
  remarks:       "",
};

// Inject responsive CSS once
const mobileCSS = `
  .docs-container .docs-doc-eye-tile,
  .docs-container a.docs-doc-eye-tile,
  .docs-container button.docs-doc-eye-tile{
    position:relative;appearance:none;font-family:Arial,sans-serif;font-size:12px;font-weight:700;letter-spacing:.02em;
    display:inline-flex !important;align-items:center;justify-content:center;min-width:44px;min-height:44px;padding:9px 12px;
    border-radius:7px;cursor:pointer;border:none !important;background-color:#FFC107 !important;background:#FFC107 !important;
    color:#111 !important;line-height:0;
    box-shadow:0 1px 3px rgba(0,0,0,.16),0 0 0 1px rgba(0,0,0,.04) !important;
    touch-action:manipulation;-webkit-tap-highlight-color:rgba(0,0,0,.08);
    transition:transform .12s ease,box-shadow .12s ease,background-color .12s ease;
    text-decoration:none;vertical-align:middle;box-sizing:border-box;
  }
  .docs-container .docs-doc-eye-tile svg{width:16px !important;height:16px !important;flex-shrink:0;color:#111 !important;fill:#111 !important}
  .docs-container .docs-doc-eye-tile:hover:not(:disabled){background-color:#f0b800 !important;background:#f0b800 !important;box-shadow:0 2px 7px rgba(0,0,0,.2),0 0 0 1px rgba(0,0,0,.06) !important;transform:translateY(-1px)}
  .docs-container .docs-doc-eye-tile:active:not(:disabled){transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,.14) !important}
  .docs-container .docs-doc-eye-tile--open{background-color:#f0b800 !important;background:#f0b800 !important;color:#111 !important;box-shadow:0 0 0 2px #1a1a1a,0 2px 7px rgba(0,0,0,.18) !important}
  .docs-container .docs-doc-eye-tile:disabled{opacity:.52;cursor:not-allowed;transform:none!important}
  .docs-container .docs-doc-eye-tile:focus-visible{outline:2px solid #1a1a1a;outline-offset:2px}
  @media (max-width: 768px) {
    .docs-container {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow-x: hidden !important;
    }
    .docs-form-grid { grid-template-columns: 1fr !important; }
    .docs-doctype-grid {
      grid-template-columns: minmax(0, 1fr) !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }
    .docs-doctype-chk {
      display: flex !important;
      flex-direction: row !important;
      align-items: flex-start !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow-wrap: break-word !important;
      word-break: normal !important;
    }
    .docs-doctype-chk input[type="checkbox"] {
      width: auto !important;
      max-width: none !important;
      flex-shrink: 0 !important;
      margin-top: 2px !important;
    }
    .docs-doctype-chk span {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      overflow-wrap: break-word !important;
      word-break: normal !important;
    }
    .docs-docblock-body { grid-template-columns: 1fr !important; }
    .docs-filter-bar { flex-direction: column !important; gap: 10px !important; }
    .docs-filter-group { width: 100% !important; }
    .docs-filter-select, .docs-filter-input { width: 100% !important; min-width: unset !important; box-sizing: border-box; }
    .docs-table-bar { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
    .docs-sel-badge { flex-wrap: wrap !important; gap: 6px !important; }
    .docs-btn-row { flex-direction: column !important; }
    .docs-btn-row button { width: 100% !important; text-align: center !important; }
    .docs-modal-wide { max-width: 100% !important; margin: 0 !important; border-radius: 0 !important; max-height: 100vh !important; height: 100% !important; }
    .docs-modal-overlay { align-items: flex-end !important; padding: 0 !important; }
    .docs-mob-list {
      padding: 0 8px 8px !important;
      box-sizing: border-box !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    .docs-mob-toolbar {
      padding: 4px 8px 0 !important;
      box-sizing: border-box !important;
    }
    .docs-mob-sec {
      border: 1px solid #ddd !important;
      border-radius: 6px !important;
      margin-bottom: 6px !important;
      background: #fff !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    .docs-mob-sec-sel {
      outline: 2px solid #FFC107 !important;
      border-color: #000 !important;
    }
    .docs-mob-sec-head {
      display: flex !important;
      align-items: flex-start !important;
      gap: 6px !important;
      padding: 6px 8px !important;
      background: #FFC107 !important;
      border-bottom: 1px solid #000 !important;
      box-sizing: border-box !important;
    }
    .docs-mob-sec-head-main {
      flex: 1 !important;
      min-width: 0 !important;
    }
    .docs-mob-sec-pills {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 3px !important;
      justify-content: flex-end !important;
      align-items: center !important;
      max-width: 42% !important;
    }
    .docs-mob-dense-grid {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 3px !important;
      padding: 3px 4px 4px !important;
      background: #f5f5f5 !important;
      box-sizing: border-box !important;
      width: 100% !important;
      align-items: stretch !important;
    }
    .docs-vm-doc-table { width: 100% !important; table-layout: fixed !important; }
    .docs-vm-doc-table .docs-col-doc { width: 10% !important; }
    .docs-doc-cell { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
    .docs-doc-eye-wrap { margin: 0 auto !important; }
  }
  @media (max-width: 480px) {
    .docs-doctype-grid { grid-template-columns: 1fr !important; }
    .docs-page-header { font-size: 16px !important; padding: 12px 14px !important; }
    .docs-form-wrapper { padding: 12px !important; }
  }
`;

if (typeof document !== "undefined") {
  let style = document.getElementById("docs-mobile-css");
  if (!style) {
    style = document.createElement("style");
    style.id = "docs-mobile-css";
    document.head.appendChild(style);
  }
  style.textContent = mobileCSS;
}

export default function Documents() {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [form,        setForm]        = useState(emptyForm);

  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [docData,      setDocData]      = useState({});

  const [saving,      setSaving]      = useState(false);
  const fileRefs = useRef({});

  // Filter state
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterDocType, setFilterDocType] = useState("");
  const [filterMonth,   setFilterMonth]   = useState("");
  const [filterYear,    setFilterYear]    = useState("");
  const [filterStatus,  setFilterStatus]  = useState("");
  const [searchText,    setSearchText]    = useState("");

  // Table row multi-select
  const [selectedRows, setSelectedRows] = useState(new Set());

  // History modal
  const [histOpen,    setHistOpen]    = useState(false);
  const [histTitle,   setHistTitle]   = useState("");
  const [histRows,    setHistRows]    = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [entryFormOpen, setEntryFormOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const load = () => {
    setLoading(true);
    axios.get(`${BASE}/onboarding/all-vehicles`)
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : [];
        setRows(buildVehicleRows(data, BASE));
      })
      .catch(() => { setRows([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const toggleDocType = (key) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setDocData((d) => { const n = { ...d }; delete n[key]; return n; });
      } else {
        next.add(key);
        setDocData((d) => ({ ...d, [key]: emptyDocEntry() }));
      }
      return next;
    });
  };

  const handleDocDataChange = (key, field, value) => {
    setDocData((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  };

  const handleDocFile = (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["application/pdf","image/jpeg","image/png"].includes(file.type)) {
      alert("Only PDF, JPG, PNG allowed.");
      e.target.value = "";
      return;
    }
    setDocData((d) => ({ ...d, [key]: { ...d[key], file } }));
  };

  const handleClear = useCallback(() => {
    setForm(emptyForm);
    setSelectedDocs(new Set());
    setDocData({});
    Object.values(fileRefs.current).forEach((ref) => { if (ref) ref.value = ""; });
  }, []);

  const closeEntryForm = useCallback(() => {
    setEntryFormOpen(false);
    handleClear();
  }, [handleClear]);

  const entryFirstFieldRef = useRef(null);

  useEffect(() => {
    if (!entryFormOpen) return;
    const t = window.setTimeout(() => entryFirstFieldRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [entryFormOpen]);

  useEffect(() => {
    if (!entryFormOpen || saving || histOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeEntryForm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entryFormOpen, saving, histOpen, closeEntryForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicleNumber)     { alert("Vehicle Number is required."); return; }
    if (selectedDocs.size === 0) { alert("Select at least one Document Type."); return; }
    for (const key of selectedDocs) {
      const dd = docData[key] || {};
      if (!dd.endDate) {
        const label = DOC_TYPES.find(d => d.key === key)?.label || key;
        alert(`End Date (Valid Upto) is required for: ${label}`);
        return;
      }
    }
    setSaving(true);
    try {
      for (const key of selectedDocs) {
        const dd = docData[key] || {};
        const fd = new FormData();
        fd.append("vehicleNumber", form.vehicleNumber);
        fd.append("documentKey",   key);
        fd.append("issueDate",     dd.fromDate  || "");
        fd.append("expiryDate",    dd.endDate);
        fd.append("remarks",       form.remarks || "");
        fd.append("submitDate",    form.submitDate || "");
        if (dd.file) fd.append("documentFile", dd.file);
        await axios.post(`${BASE}/onboarding/renew-document`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      alert(`${selectedDocs.size} document(s) renewed successfully!`);
      closeEntryForm();
      load();
    } catch {
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const daysLeft = (d) => {
    if (!d) return 9999;
    return Math.ceil((new Date(d) - new Date()) / 86400000);
  };
  const getStatusLabel = (d) => {
    const dl = daysLeft(d);
    if (dl < 0)    return "Expired";
    if (dl < 30)   return "Expiring Soon";
    if (dl < 9999) return "Valid";
    return "—";
  };
  const statusBadge = (d) => {
    const dl = daysLeft(d);
    if (dl < 0)    return <span style={S.badgeRed}>Expired</span>;
    if (dl < 30)   return <span style={S.badgeYellow}>{dl}d left</span>;
    if (dl < 9999) return <span style={S.badgeGreen}>Valid</span>;
    return           <span style={S.badgeGrey}>—</span>;
  };

  const openHistory = async ({ vehicleNumber, documentKey, documentType }) => {
    setHistTitle(`${vehicleNumber} — ${documentType}`);
    setHistOpen(true);
    setHistLoading(true);
    setHistRows([]);
    try {
      const r = await axios.get(
        `${BASE}/onboarding/document-history/${vehicleNumber}/${documentKey}`
      );
      setHistRows(Array.isArray(r.data) ? r.data : []);
    } catch {
      setHistRows([]);
    } finally {
      setHistLoading(false);
    }
  };

  const filtered = rows.filter((row) => {
    if (searchText) {
      const s = searchText.toLowerCase();
      const docMatch = DOC_TYPES.some(({ key, label }) =>
        (label || "").toLowerCase().includes(s) ||
        (DOC_SHORT[key] || "").toLowerCase().includes(s)
      );
      if (
        !(row.vehicleNumber || "").toLowerCase().includes(s) &&
        !(row.registerName || "").toLowerCase().includes(s) &&
        !docMatch
      ) return false;
    }
    if (filterVehicle && row.vehicleNumber !== filterVehicle) return false;

    const dateStatusOk = (endDate) => {
      if (filterStatus && getStatusLabel(endDate) !== filterStatus) return false;
      if (endDate) {
        const d = new Date(endDate);
        if (filterMonth && String(d.getMonth() + 1) !== filterMonth) return false;
        if (filterYear && String(d.getFullYear()) !== filterYear) return false;
      } else if (filterMonth || filterYear) {
        return false;
      }
      return true;
    };

    if (filterDocType) {
      const d = row.docs[filterDocType];
      if (!d) return false;
      return dateStatusOk(d.endDate);
    }
    if (filterStatus || filterMonth || filterYear) {
      return DOC_TYPES.some(({ key }) => dateStatusOk(row.docs[key].endDate));
    }
    return true;
  });

  const clearFilters = () => {
    setFilterVehicle(""); setFilterDocType(""); setFilterMonth("");
    setFilterYear(""); setFilterStatus(""); setSearchText("");
    setSelectedRows(new Set());
  };

  const rowKey    = (r) => r.vehicleNumber;
  const toggleRow = (r) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAll   = () => {
    if (selectedRows.size === filtered.length && filtered.length > 0)
      setSelectedRows(new Set());
    else
      setSelectedRows(new Set(filtered.map(rowKey)));
  };
  const allSelected  = filtered.length > 0 && selectedRows.size === filtered.length;
  const someSelected = selectedRows.size > 0;

  const downloadCSV = () => {
    const selectedVehicles = filtered.filter((r) => selectedRows.has(rowKey(r)));
    if (!selectedVehicles.length) { alert("Please select at least one row to download."); return; }
    const headers = ["Vehicle No", "Owner", "Document Type", "From Date", "Valid Upto", "Status", "Source", "Remarks"];
    const lines = [headers.join(",")];
    for (const row of selectedVehicles) {
      for (const { key } of DOC_TYPES) {
        const r = row.docs[key];
        lines.push([
          row.vehicleNumber,
          row.registerName || "",
          r.documentType,
          r.fromDate ? fmt(r.fromDate) : "",
          r.endDate ? fmt(r.endDate) : "",
          getStatusLabel(r.endDate),
          r.source || "",
          `"${(r.remarks || "").replace(/"/g, '""')}"`,
        ].join(","));
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `vehicle_documents_${today()}.csv`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  const uniqueVehicles = [...new Set(rows.map(r => r.vehicleNumber))].filter(Boolean).sort();

  const C = {
    yellow:"#FFC107", black:"#000", white:"#fff",
    border:"1.5px solid #000", borderLight:"1px solid #f0f0f0",
    borderYellow:"2px solid #FFC107", radius:"4px",
    fontFamily:"Arial, sans-serif", fontSize:"14px", fontSizeSm:"13px",
  };
  const S = {
    container:    { ...vmPageShell(isMobile, { marginRightPx: isMobile ? 0 : 10 }), fontFamily:C.fontFamily, backgroundColor:C.white, color:C.black, minHeight:"calc(100vh - 70px)", boxShadow:"0px 4px 8px rgba(0,0,0,0.1)" },
    pageHeader:   { backgroundColor:C.yellow, color:C.black, padding:"16px 20px", fontWeight:"bold", fontSize:20, letterSpacing:1, textTransform:"uppercase" },
    formWrapper:  { padding: isMobile ? 12 : 20 },
    sectionTitle: { fontWeight:"bold", fontSize:C.fontSizeSm, color:C.black, borderBottom:C.borderYellow, paddingBottom:6, marginBottom:16, marginTop:20, textTransform:"uppercase", letterSpacing:"0.5px" },
    formGrid:     { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:16, marginBottom:16 },
    label:        { fontWeight:"bold", fontSize:C.fontSizeSm, marginBottom:5, display:"block", color:C.black },
    input:        { width:"100%", padding:"9px 10px", border:C.border, borderRadius:C.radius, fontSize:C.fontSize, boxSizing:"border-box", color:C.black, backgroundColor:C.white, outline:"none", fontFamily:C.fontFamily },
    fileSuccess:  { fontSize:12, color:"green", marginTop:4 },
    buttonRow:    { display:"flex", gap:12, justifyContent:"flex-end", marginTop:10, paddingBottom:10, flexWrap:"wrap" },
    btnBlack:     { padding:"10px 28px", border:"none", borderRadius:C.radius, cursor:"pointer", backgroundColor:C.black, color:C.white, fontWeight:"bold", fontSize:C.fontSize, fontFamily:C.fontFamily, minHeight:44, touchAction:"manipulation" },
    btnYellow:    { padding:"10px 28px", border:"none", borderRadius:C.radius, cursor:"pointer", backgroundColor:C.yellow, color:C.black, fontWeight:"bold", fontSize:C.fontSize, fontFamily:C.fontFamily, minHeight:44, touchAction:"manipulation" },
    btnOutline:   { padding:"10px 16px", border:C.border, borderRadius:C.radius, cursor:"pointer", backgroundColor:C.white, color:C.black, fontWeight:"bold", fontSize:C.fontSize, fontFamily:C.fontFamily, minHeight:44, touchAction:"manipulation" },
    docTypeGrid:  { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 175px), 1fr))", gap:8, marginBottom:16, width:"100%", boxSizing:"border-box" },
    docTypeChk:   { display:"flex", flexDirection:"row", alignItems:"flex-start", gap:10, padding:"10px 12px", border:C.border, borderRadius:C.radius, cursor:"pointer", userSelect:"none", fontSize:C.fontSizeSm, backgroundColor:C.white, minHeight:44, touchAction:"manipulation", width:"100%", minWidth:0, maxWidth:"100%", boxSizing:"border-box", overflowWrap:"break-word", wordBreak:"normal" },
    docTypeChkSel:{ display:"flex", flexDirection:"row", alignItems:"flex-start", gap:10, padding:"10px 12px", border:"2px solid #000", borderRadius:C.radius, cursor:"pointer", userSelect:"none", fontSize:C.fontSizeSm, backgroundColor:C.yellow, fontWeight:"bold", minHeight:44, touchAction:"manipulation", width:"100%", minWidth:0, maxWidth:"100%", boxSizing:"border-box", overflowWrap:"break-word", wordBreak:"normal" },
    checkbox:     { width:18, height:18, minWidth:18, maxWidth:18, cursor:"pointer", accentColor:C.black, flexShrink:0, flexGrow:0 },
    docBlock:     { border:C.border, borderRadius:C.radius, marginBottom:12, overflow:"hidden" },
    docBlockHdr:  { backgroundColor:C.yellow, padding:"8px 14px", fontWeight:"bold", fontSize:C.fontSizeSm, borderBottom:C.border },
    docBlockBody: { padding:"14px", display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:14 },
    filterBar:    { padding:"14px 20px", borderBottom:"1.5px solid #e0e0e0", display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-end", backgroundColor:C.white },
    filterGroup:  { display:"flex", flexDirection:"column", gap:4 },
    filterLabel:  { fontSize:11, fontWeight:"bold", color:"#555", textTransform:"uppercase", letterSpacing:"0.5px" },
    filterSelect: { padding:"9px 10px", border:C.border, borderRadius:C.radius, fontSize:13, color:C.black, backgroundColor:C.white, cursor:"pointer", minWidth:155, fontFamily:C.fontFamily, minHeight:42, touchAction:"manipulation", boxSizing:"border-box" },
    filterInput:  { padding:"9px 10px", border:C.border, borderRadius:C.radius, fontSize:13, color:C.black, backgroundColor:C.white, minWidth:195, fontFamily:C.fontFamily, minHeight:42 },
    divider:      { borderTop:"2px solid #f0f0f0", margin:"4px 0" },
    tableBar:     { padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, borderBottom:"1.5px solid #e0e0e0" },
    tableCount:   { fontSize:13, color:"#555" },
    selBadge:     { display:"inline-flex", alignItems:"center", gap:10, marginLeft:12, padding:"5px 12px", border:C.border, borderRadius:C.radius, fontSize:13, backgroundColor:C.white, flexWrap:"wrap" },
    table:        { ...getVmRecordTableStyle(isMobile, DOC_RECORD_COL_COUNT), width: "100%" },
    docCell:        { display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"5px 4px", border:"1px solid #e8e8e8", borderRadius:5, backgroundColor:"#fafafa", fontSize:11, boxSizing:"border-box", width:"100%", minWidth:0 },
    docCellRenewed: { border:"2px solid #2e7d32", backgroundColor:"#e8f5e9" },
    docCellTags:    { display:"flex", justifyContent:"center", minHeight:14 },
    docCellExpiry:  { fontSize:11, textAlign:"center", lineHeight:1.2 },
    docCellStatus:  { display:"flex", justifyContent:"center", minHeight:22 },
    docCellActions: { display:"flex", justifyContent:"center", marginTop:2 },
    srcTagCompact:  { fontSize:9, fontWeight:700, color:"#555", backgroundColor:"#ececec", border:"1px solid #ccc", borderRadius:3, padding:"0 4px", lineHeight:1.3 },
    srcTagGreenCompact:{ fontSize:9, fontWeight:700, color:"#1b5e20", backgroundColor:"#e8f5e9", border:"1px solid #66bb6a", borderRadius:3, padding:"0 4px", lineHeight:1.3 },
    srcTagMuted:    { fontSize:9, color:"#bbb" },
    th:           getVmRecordThStyle(isMobile),
    td:           { ...getVmRecordTdStyle(isMobile), color:C.black },
    badgeGreen:   { backgroundColor:"#e8f5e9", color:"#2e7d32", padding:"3px 10px", borderRadius:12, fontWeight:"bold", fontSize:12, border:"1px solid #2e7d32" },
    badgeYellow:  { backgroundColor:"#fff8e1", color:"#f57f17", padding:"3px 10px", borderRadius:12, fontWeight:"bold", fontSize:12, border:"1px solid #f57f17" },
    badgeRed:     { backgroundColor:"#ffebee", color:"#c62828", padding:"3px 10px", borderRadius:12, fontWeight:"bold", fontSize:12, border:"1px solid #c62828" },
    badgeGrey:    { backgroundColor:"#f5f5f5", color:"#888",    padding:"3px 10px", borderRadius:12, fontWeight:"bold", fontSize:12, border:"1px solid #ccc" },
    badgeBlue:    { backgroundColor:"#e3f2fd", color:"#1565c0", padding:"3px 10px", borderRadius:12, fontWeight:"bold", fontSize:12, border:"1px solid #1565c0", cursor:"pointer", userSelect:"none" },
    srcTag:       { fontSize:11, color:"#555", backgroundColor:"#f5f5f5", border:"1px solid #ddd", borderRadius:8, padding:"1px 7px", marginLeft:5 },
    srcTagGreen:  { fontSize:11, color:"#2e7d32", backgroundColor:"#e8f5e9", border:"1px solid #2e7d32", borderRadius:8, padding:"1px 7px", marginLeft:5 },
    docVehicleGrid: { display:"flex", flexWrap:"wrap", gap:8, alignItems:"stretch", maxWidth:"100%", minWidth:0 },
    docTile:        { display:"flex", flexDirection:"column", gap:4, padding:"6px 8px", border:"1px solid #ddd", borderRadius:6, backgroundColor:"#fafafa", minWidth:88, maxWidth:140, fontSize:11, boxSizing:"border-box" },
    docTileRenewed: { border:"2px solid #2e7d32", backgroundColor:"#e8f5e9" },
    docTileHdr:     { fontWeight:700, fontSize:11, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" },
    docTileRow:     { fontSize:11, color:"#333" },
    docTileActions: { display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginTop:2 },
    btnPrev:        { fontSize:10, fontWeight:"bold", padding:"4px 8px", borderRadius:4, border:"1px solid #1565c0", background:"#e3f2fd", color:"#1565c0", cursor:"pointer", fontFamily:C.fontFamily, minHeight:32, touchAction:"manipulation" },
    btnPrevDisabled:{ opacity:0.45, cursor:"not-allowed" },
    mobList:        { padding:"0 8px 6px", boxSizing:"border-box", width:"100%", maxWidth:"100%" },
    mobToolbar:     { padding:"4px 8px 0", display:"flex", alignItems:"center", gap:8, boxSizing:"border-box" },
    mobToolbarLbl:  { fontSize:12, fontWeight:"bold", color:C.black, fontFamily:C.fontFamily },
    mobLoading:     { textAlign:"center", color:"#888", padding:"16px 12px", fontSize:13, fontFamily:C.fontFamily },
    mobEmpty:       { textAlign:"center", color:"#888", padding:"16px 12px", fontSize:13, fontFamily:C.fontFamily },
    mobSec:         { border:"1px solid #ddd", borderRadius:5, overflow:"hidden", marginBottom:5, backgroundColor:C.white, boxSizing:"border-box", width:"100%", maxWidth:"100%" },
    mobSecSel:      { outline:"2px solid #FFC107", borderColor:"#000" },
    mobSecHead:     { display:"flex", alignItems:"flex-start", gap:5, padding:"5px 6px", backgroundColor:C.yellow, borderBottom:"1px solid #000", boxSizing:"border-box" },
    mobSecHeadMain: { flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:2 },
    mobHeadTop:     { display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:6, width:"100%", minWidth:0 },
    mobVehicleNo:   { fontSize:14, fontWeight:"bold", letterSpacing:"0.02em", color:C.black, fontFamily:C.fontFamily, lineHeight:1.1, wordBreak:"break-word" },
    mobOwner:       { fontSize:9, color:"#222", fontFamily:C.fontFamily, lineHeight:1.2, wordBreak:"break-word", marginTop:1 },
    mobPillsWrap:   { display:"flex", flexWrap:"wrap", gap:3, justifyContent:"flex-end", alignItems:"center", flexShrink:0, maxWidth:"46%" },
    mobPill:        { fontSize:8, fontWeight:700, padding:"2px 5px", borderRadius:999, border:"1px solid #bbb", backgroundColor:C.white, color:"#333", fontFamily:C.fontFamily, lineHeight:1.2, whiteSpace:"nowrap" },
    mobPillExp:     { border:"1px solid #c62828", backgroundColor:"#ffebee", color:"#c62828" },
    mobPillSoon:    { border:"1px solid #f57f17", backgroundColor:"#fff8e1", color:"#f57f17" },
    mobPillOk:      { border:"1px solid #2e7d32", backgroundColor:"#e8f5e9", color:"#2e7d32" },
    mobDenseGrid:   { display:"grid", gridTemplateColumns:"repeat(2, minmax(0, 1fr))", gap:3, padding:"3px 4px 4px", backgroundColor:"#f5f5f5", boxSizing:"border-box", width:"100%", alignItems:"stretch" },
    overlay:      { position:"fixed", inset:0, backgroundColor:"rgba(0,0,0,.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 },
    modalWide:    { backgroundColor:C.white, borderRadius:6, width:"100%", maxWidth:900, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 4px 24px rgba(0,0,0,.2)" },
    modalHdr:     { backgroundColor:C.yellow, color:C.black, padding:"14px 20px", fontWeight:"bold", fontSize:C.fontSize, borderRadius:"6px 6px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:1 },
    modalBody:    { padding:20 },
    btnRow:       { display:"flex", gap:12, justifyContent:"flex-end", marginTop:20 },
    entryPanelHdr: {
      backgroundColor: "transparent",
      color: C.black,
      padding: "0 0 10px 0",
      fontWeight: "bold",
      fontSize: C.fontSize,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
      borderBottom: "1px solid #e0e0e0",
    },
  };

  const CloseBtn = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:C.black, padding:"4px 8px", minHeight:44, minWidth:44, touchAction:"manipulation" }}
    >
      ✕
    </button>
  );

  return (
    <div className="docs-container" style={S.container}>
      <div className="docs-page-header" style={S.pageHeader}>VEHICLE DOCUMENTS</div>

      {/* ── FORM ─────────────────────────────────────────────────────────── */}
      <div className="docs-form-wrapper" style={S.formWrapper}>
        {!entryFormOpen ? (
          <>
            <div style={S.sectionTitle}>New document</div>
            <div style={{ marginBottom: 16 }}>
              <button
                type="button"
                style={S.btnYellow}
                onClick={() => setEntryFormOpen(true)}
                aria-expanded={false}
                aria-controls="docs-entry-panel"
              >
                Add Documents
              </button>
            </div>
          </>
        ) : (
        <div id="docs-entry-panel" role="region" aria-labelledby="docs-entry-title">
          <div style={S.entryPanelHdr}>
            <span id="docs-entry-title">Add documents</span>
            <button
              type="button"
              onClick={closeEntryForm}
              disabled={saving}
              aria-label="Close add documents form"
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: saving ? "not-allowed" : "pointer",
                color: C.black,
                fontFamily: C.fontFamily,
                padding: "4px 8px",
                minHeight: 44,
                minWidth: 44,
                touchAction: "manipulation",
                opacity: saving ? 0.5 : 1,
              }}
            >
              ✕
            </button>
          </div>
        <form onSubmit={handleSubmit} aria-busy={saving}>
          <div className="docs-form-grid" style={S.formGrid}>
            <div>
              <label style={S.label}>Submit Date:</label>
              <input
                ref={entryFirstFieldRef}
                type="date"
                style={S.input}
                name="submitDate"
                value={form.submitDate}
                onChange={handleFormChange}
                required
              />
            </div>
            <div>
              <label style={S.label}>Vehicle Number:</label>
              <FleetVehicleSelect
                name="vehicleNumber"
                value={form.vehicleNumber}
                onChange={handleFormChange}
                style={S.input}
                required
              />
            </div>
            <div>
              <label style={S.label}>Remarks:</label>
              <input type="text" style={S.input} name="remarks"
                value={form.remarks} onChange={handleFormChange} />
            </div>
          </div>

          {/* Document Type — multi-checkbox tiles */}
          <div style={S.sectionTitle}>Document types</div>
          <div className="docs-doctype-grid" style={S.docTypeGrid}>
            {DOC_TYPES.map(({ key, label }) => {
              const checked = selectedDocs.has(key);
              return (
                <label key={key} className="docs-doctype-chk" style={checked ? S.docTypeChkSel : S.docTypeChk}>
                  <input
                    type="checkbox"
                    style={S.checkbox}
                    checked={checked}
                    onChange={() => toggleDocType(key)}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>

          {/* Per-doc validity + upload */}
          {selectedDocs.size > 0 && (
            <>
              <div style={S.sectionTitle}>Validity &amp; upload</div>
              {DOC_TYPES.filter(d => selectedDocs.has(d.key)).map(({ key, label }) => {
                const dd = docData[key] || emptyDocEntry();
                return (
                  <div key={key} style={S.docBlock}>
                    <div style={S.docBlockHdr}>{label}</div>
                    <div className="docs-docblock-body" style={S.docBlockBody}>
                      <div>
                        <label style={S.label}>From Date:</label>
                        <input type="date" style={S.input}
                          value={dd.fromDate}
                          onChange={(e) => handleDocDataChange(key, "fromDate", e.target.value)} />
                      </div>
                      <div>
                        <label style={S.label}>End Date (Valid Upto): *</label>
                        <input type="date" style={S.input}
                          value={dd.endDate}
                          onChange={(e) => handleDocDataChange(key, "endDate", e.target.value)} />
                      </div>
                      <div>
                        <label style={S.label}>{label} (PDF / JPG / PNG):</label>
                        <input
                          ref={(el) => (fileRefs.current[key] = el)}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocFile(key, e)}
                          style={{ fontSize:13, width:"100%", boxSizing:"border-box" }}
                        />
                        {dd.file && <p style={S.fileSuccess}>✓ {dd.file.name}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          <div className="docs-btn-row" style={S.buttonRow}>
            <button type="button" style={S.btnBlack} onClick={closeEntryForm} disabled={saving}>
              Cancel
            </button>
            <button type="button" style={S.btnBlack} onClick={handleClear} disabled={saving}>
              Clear
            </button>
            <button type="submit" style={S.btnYellow} disabled={saving}>
              {saving ? "Saving…" : `Submit${selectedDocs.size > 1 ? ` (${selectedDocs.size})` : ""}`}
            </button>
          </div>
        </form>
        </div>
        )}
      </div>

      <div style={S.divider} />

      {/* ── FILTER BAR ───────────────────────────────────────────────────── */}
      <div className="docs-filter-bar" style={S.filterBar}>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Search</span>
          <input type="text" placeholder="Search…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            className="docs-filter-input" style={S.filterInput} />
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Vehicle Number</span>
          <select className="docs-filter-select" style={S.filterSelect} value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)}>
            <option value="">All Vehicles</option>
            {uniqueVehicles.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Document Type</span>
          <select className="docs-filter-select" style={S.filterSelect} value={filterDocType} onChange={(e) => setFilterDocType(e.target.value)}>
            <option value="">All Types</option>
            {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Expiry Month</span>
          <select className="docs-filter-select" style={S.filterSelect} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
            {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Expiry Year</span>
          <select className="docs-filter-select" style={S.filterSelect} value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
            {YEARS.map((y) => <option key={y} value={y}>{y === "" ? "All Years" : y}</option>)}
          </select>
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>Status</span>
          <select className="docs-filter-select" style={S.filterSelect} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="Valid">Valid</option>
            <option value="Expiring Soon">Expiring Soon</option>
            <option value="Expired">Expired</option>
          </select>
        </div>
        <div className="docs-filter-group" style={S.filterGroup}>
          <span style={S.filterLabel}>&nbsp;</span>
          <button style={S.btnOutline} onClick={clearFilters}>Clear Filters</button>
        </div>
      </div>

      {/* ── TABLE TOOLBAR ────────────────────────────────────────────────── */}
      <div className="docs-table-bar" style={S.tableBar}>
        <span style={S.tableCount}>
          Showing <b>{filtered.length}</b> vehicle{filtered.length !== 1 ? "s" : ""}
          {someSelected && (
            <span className="docs-sel-badge" style={S.selBadge}>
              <b>{selectedRows.size}</b> selected
              <button style={{ ...S.btnYellow, padding:"6px 14px", fontSize:12 }} onClick={downloadCSV}>
                Download CSV
              </button>
              <button style={{ ...S.btnOutline, padding:"6px 12px", fontSize:12 }} onClick={() => setSelectedRows(new Set())}>
                Deselect All
              </button>
            </span>
          )}
        </span>
        {!someSelected && (
          <button style={S.btnBlack} onClick={downloadCSV}>Download CSV</button>
        )}
      </div>

      {/* ── VEHICLE RECORDS (desktop + mobile: horizontal scroll table) ─── */}
      <div style={vmTableScrollWrap}>
        <table className="docs-vm-doc-table" style={S.table}>
          {isMobile ? vmRecordColGroupReact(DOC_RECORD_COL_COUNT) : <DocsDesktopColGroup />}
          <thead>
            <tr>
              <th style={{ ...S.th, textAlign: "center" }}>
                <input type="checkbox" style={S.checkbox} checked={allSelected} onChange={toggleAll} aria-label="Select all" />
              </th>
              <th style={S.th}>Vehicle</th>
              <th style={S.th}>Owner</th>
              {DOC_TYPES.map(({ key }) => (
                <th key={key} style={S.th}>{DOC_SHORT[key]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={DOC_RECORD_COL_COUNT} style={{ ...S.td, textAlign: "center", color: "#aaa", padding: 28 }}>
                  Loading…
                </td>
              </tr>
            ) : filtered.length > 0 ? (
              filtered.map((row, i) => {
                const k = rowKey(row);
                const isSel = selectedRows.has(k);
                return (
                  <tr
                    key={k || i}
                    style={{
                      backgroundColor: isSel ? "#fffde7" : vmRecordRowBg(i),
                      outline: isSel ? "2px solid #FFC107" : "none",
                    }}
                  >
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <input type="checkbox" style={S.checkbox} checked={isSel} onChange={() => toggleRow(row)} />
                    </td>
                    <td style={S.td}><b>{row.vehicleNumber}</b></td>
                    <td style={S.td}>{row.registerName}</td>
                    {DOC_TYPES.map(({ key, label }) => (
                      <td key={key} style={{ ...S.td, verticalAlign: "top", padding: "6px 4px" }}>
                        <DocumentCell
                          row={row}
                          docKey={key}
                          label={label}
                          S={S}
                          fmt={fmt}
                          statusBadge={statusBadge}
                          openHistory={openHistory}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={DOC_RECORD_COL_COUNT} style={{ ...S.td, textAlign: "center", color: "#aaa", padding: 28 }}>
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── HISTORY MODAL ────────────────────────────────────────────────── */}
      {histOpen && (
        <div className="docs-modal-overlay" style={S.overlay} onClick={() => setHistOpen(false)}>
          <div className="docs-modal-wide" style={S.modalWide} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontSize:14 }}>All Records — {histTitle}</span>
              <CloseBtn onClick={() => setHistOpen(false)} />
            </div>
            <div style={S.modalBody}>
              {histLoading ? (
                <div style={{ textAlign:"center", color:"#aaa", padding:28 }}>Loading…</div>
              ) : histRows.length === 0 ? (
                <div style={{ textAlign:"center", color:"#aaa", padding:28 }}>
                  No history yet.<br/>
                  <small>Documents uploaded during onboarding appear here automatically.</small>
                </div>
              ) : (
                <div style={vmTableScrollWrap}>
                  <table style={{ ...getVmRecordTableStyle(isMobile, 8), marginTop: 0 }}>
                    {isMobile ? vmRecordColGroupReact(8) : null}
                    <thead>
                      <tr>
                        {["#","Source","From Date","Valid Upto","Uploaded On","Status","File","Remarks"].map((h, hi) => (
                          <th key={hi} style={{ ...S.th, fontSize:12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...histRows].reverse().map((h, i) => (
                        <tr key={i} style={{ backgroundColor: vmRecordRowBg(i) }}>
                          <td style={{ ...S.td, fontSize:12 }}>{histRows.length - i}</td>
                          <td style={{ ...S.td, fontSize:12 }}>
                            {h.source === "onboarding"
                              ? <span style={S.srcTag}>onboarding</span>
                              : <span style={S.srcTagGreen}>renewal</span>}
                          </td>
                          <td style={{ ...S.td, fontSize:12 }}>{fmt(h.issueDate)}</td>
                          <td style={{ ...S.td, fontSize:12, fontWeight:"bold" }}>{fmt(h.expiryDate)}</td>
                          <td style={{ ...S.td, fontSize:12 }}>{fmt(h.uploadedAt)}</td>
                          <td style={{ ...S.td, fontSize:12 }}>{statusBadge(h.expiryDate)}</td>
                          <td style={{ ...S.td, fontSize:12 }}>
                            {h.fileUrl ? (
                              <DocumentEyeTileLink
                                href={resolveUploadUrl(BASE, h.fileUrl)}
                                ariaLabel="View file"
                                title="View file"
                              />
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ ...S.td, fontSize:12 }}>{h.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ ...S.btnRow }}>
                <button style={{ ...S.btnBlack, minWidth:100 }} onClick={() => setHistOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}